import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { usePageMeta } from "../lib/usePageMeta";
import {
  GraduationCap, Plane, ShieldCheck, FileCheck2, Wallet, Award, ClipboardList,
  ArrowRight, Sparkles, Globe2, BookOpen, Users, Home, Briefcase, Languages,
  MapPin, Sun, Leaf, HeartHandshake, Clock,
} from "lucide-react";
import SchoolQuoteModal from "../components/SchoolQuoteModal";
import { MarcasBlock } from "./Portfolio";
import "./Portfolio.css";

const HERO_IMG = "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1600&q=70";
const LIB_IMG = "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?auto=format&fit=crop&w=900&q=70";
const CITY_IMG = "https://images.unsplash.com/photo-1519677100203-a0e668c92439?auto=format&fit=crop&w=900&q=70";
const CTA_IMG = "https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=1600&q=70";

// PROGRAM TYPES — the six pillars of what Orion sells as intercâmbio
const PROGRAMS = [
  {
    icon: Languages,
    title: "Curso de Idiomas",
    body: "Inglês, espanhol, francês, italiano ou alemão em escolas certificadas. Turmas multiculturais, aulas 20 a 30h/semana, do básico ao advanced.",
  },
  {
    icon: BookOpen,
    title: "High School no Exterior",
    body: "1 semestre ou 1 ano cursando o ensino médio em colégio internacional. Ideal para 14 a 18 anos que querem sair transformados.",
  },
  {
    icon: Briefcase,
    title: "Estudo + Trabalho",
    body: "Programas de longa duração (25+ semanas) na Irlanda, Austrália, Canadá e Malta. Estuda de manhã, trabalha à tarde — e paga parte do intercâmbio.",
  },
  {
    icon: Home,
    title: "Au Pair",
    body: "Vive com uma família anfitriã nos EUA ou Europa, cuida das crianças e recebe stipend + curso de idioma. Programa oficial J-1/EF/Cultural Care.",
  },
  {
    icon: GraduationCap,
    title: "Graduação e Pós",
    body: "Universidade completa ou 1 semestre de mobilidade acadêmica em Portugal, Espanha, Canadá, Reino Unido e Alemanha (várias sem mensalidade).",
  },
  {
    icon: Sun,
    title: "Teen & Junior Camps",
    body: "Camps de verão para 12 a 17 anos, com aulas de idioma pela manhã e passeios/esportes à tarde. Grupo Brasil acompanhado — sonho dos pais que querem tranquilidade.",
  },
];

// DESTINATIONS with realistic pricing from Egali (Feb/2026 promotions).
// SHORT-duration programs (2 weeks) are labeled as "Intercâmbio Turístico"
// per user request. Destinations without a matching Egali promo stay
// "Sob Consulta". Every card ends with the same instalment caption.
const DESTINATIONS = [
  {
    country: "Malta",
    city: "St. Julian's",
    flag: "🇲🇹",
    from: "R$ 6.857",
    tag: "Intercâmbio Turístico",
    tagSub: "Experiência experimental",
    highlight: "2 semanas · escola + hospedagem StayWise Malta",
    duration: "2 semanas",
    programType: "curta",
    color: "#C4364C",
  },
  {
    country: "Canadá",
    city: "Vancouver",
    flag: "🇨🇦",
    from: "R$ 7.271",
    tag: "Intercâmbio Turístico",
    tagSub: "Experiência experimental",
    highlight: "2 semanas Premium · seguro + transfer + base local",
    duration: "2 semanas Premium",
    programType: "curta",
    color: "#C8352C",
  },
  {
    country: "Reino Unido",
    city: "Londres",
    flag: "🇬🇧",
    from: "R$ 7.699",
    tag: "Intercâmbio Turístico",
    tagSub: "Experiência experimental",
    highlight: "2 semanas · escola premium com social programmes",
    duration: "2 semanas",
    programType: "curta",
    color: "#5B2A83",
  },
  {
    country: "Reino Unido",
    city: "Liverpool",
    flag: "🇬🇧",
    from: "R$ 7.299",
    tag: "Intercâmbio Turístico",
    tagSub: "Experiência experimental",
    highlight: "2 semanas · escola premium One Islington",
    duration: "2 semanas",
    programType: "curta",
    color: "#3A5FCD",
  },
  {
    country: "Canadá",
    city: "Toronto",
    flag: "🇨🇦",
    from: "R$ 13.192",
    tag: "Estude Idiomas",
    tagSub: null,
    highlight: "12 semanas · base Egali com Walking Tour incluído",
    duration: "12 semanas",
    programType: "longa",
    color: "#0F7B4A",
  },
  {
    country: "Austrália",
    city: "Brisbane",
    flag: "🇦🇺",
    from: "R$ 15.580",
    tag: "Estude e Trabalhe",
    tagSub: null,
    highlight: "14 semanas · OSHC + suporte português no destino",
    duration: "14 semanas",
    programType: "longa",
    color: "#0B7285",
  },
  {
    country: "Irlanda",
    city: "Dublin",
    flag: "🇮🇪",
    from: "R$ 22.999",
    tag: "Estude e Trabalhe",
    tagSub: null,
    highlight: "25 semanas · trabalho legal 20h/sem (40h em férias)",
    duration: "25 semanas",
    programType: "longa",
    color: "#0F7B4A",
  },
  {
    country: "Estados Unidos",
    city: "NY · Miami · San Diego",
    flag: "🇺🇸",
    from: "Sob Consulta",
    tag: "Programa completo",
    tagSub: null,
    highlight: "Idiomas, universidade e Au Pair — sob medida",
    duration: "2 a 52 semanas",
    programType: "consulta",
    color: "#1A2C7F",
  },
  {
    country: "Portugal",
    city: "Lisboa · Porto · Coimbra",
    flag: "🇵🇹",
    from: "Sob Consulta",
    tag: "Graduação & Semestre",
    tagSub: null,
    highlight: "Universidade pública com anuidade a partir de € 700",
    duration: "Semestre / Graduação",
    programType: "consulta",
    color: "#B02E1C",
  },
  {
    country: "Espanha",
    city: "Madri · Barcelona · Valência",
    flag: "🇪🇸",
    from: "Sob Consulta",
    tag: "Cultura + Idioma",
    tagSub: null,
    highlight: "Espanhol nativo + universidades top mundial",
    duration: "4 a 48 semanas",
    programType: "consulta",
    color: "#F0A400",
  },
];

const HOW = [
  { step: "01", title: "Consultoria Gratuita", body: "Conversamos por WhatsApp/Zoom pra entender o momento da sua vida, orçamento e sonho. Devolvemos 2 a 3 programas comparados." },
  { step: "02", title: "Matrícula & Documentos", body: "Cuidamos da matrícula na escola parceira, coletamos documentos e organizamos o dossiê do visto — com checklist personalizado." },
  { step: "03", title: "Preparação Pré-embarque", body: "Mentoria em grupo com quem já morou fora, checklist de mala, câmbio, seguro-saúde, chip internacional e primeiros passos no destino." },
  { step: "04", title: "Suporte 24h no Destino", body: "Equipe Orion + a escola parceira acompanham durante toda a viagem. Emergência médica, mudança de hospedagem, extensão — tudo resolvido em português." },
];

const INCLUDED = [
  "Consultoria e escolha do programa ideal",
  "Matrícula em escola/universidade credenciada",
  "Assessoria completa de visto e documentação",
  "Seguro-saúde internacional pelo tempo do curso",
  "Hospedagem selecionada (casa de família, residência ou apartamento)",
  "Traslado do aeroporto no primeiro dia",
  "Chip internacional pré-ativado",
  "Suporte 24h em português durante toda a viagem",
];

const TRUST = [
  { icon: ShieldCheck, title: "Cadastur ativo", body: "Ministério do Turismo." },
  { icon: FileCheck2, title: "Contrato formal", body: "CDC + reembolsável." },
  { icon: ClipboardList, title: "Nota fiscal", body: "Emissão sobre serviço." },
  { icon: Award, title: "Escolas certificadas", body: "IALC, Eaquals, British Council." },
];

export default function Intercambio() {
  const [quoteOpen, setQuoteOpen] = React.useState(false);
  usePageMeta(
    "Intercâmbio · Viagens Orion",
    "Intercâmbio com consultoria completa: idiomas, high school, estudo + trabalho, au pair e graduação. Malta, Canadá, Irlanda, Reino Unido, Austrália. Programa turístico de 2 semanas a partir de R$ 6.857. Parcelamento facilitado."
  );

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1" data-testid="intercambio-page">
        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0e1e46] via-[#1c1149] to-[#0d0413] text-white">
          <span className="absolute inset-0 opacity-30" aria-hidden style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }} />
          <span className="absolute inset-0 bg-gradient-to-b from-[#0e1e46]/95 via-[#1c1149]/70 to-[#0d0413]/85" aria-hidden />
          <span className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-orion-gold/25 blur-3xl" aria-hidden />
          <span className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-orion-orange/20 blur-3xl" aria-hidden />

          <div className="relative container-orion py-16 md:py-28 max-w-4xl text-center mx-auto">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-5">
              <Plane size={13} /> Viagens Orion · Intercâmbio
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.02] mx-auto">
              Seu <span className="text-orion-gold">próximo capítulo</span> pode começar do outro lado do mundo.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/85 mx-auto leading-relaxed">
              Consultoria completa em intercâmbio: idiomas, high school, estudo + trabalho, au pair,
              graduação e teen camps. Escolas certificadas, visto, seguro e suporte 24h em português —
              tudo com o cuidado que a Orion coloca em cada sonho realizado.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setQuoteOpen(true)}
                className="inline-flex items-center gap-2 bg-orion-gold text-orion-purple px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#c19a3f] transition-all shadow-[0_14px_38px_rgba(216,167,73,0.45)] hover:-translate-y-0.5"
                data-testid="int-cta-quote"
              >
                <ClipboardList size={14} /> Solicitar Consultoria Gratuita
              </button>
              <a href="#destinos" className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:border-orion-gold hover:text-orion-gold transition-colors">
                Ver Destinos <ArrowRight size={14} />
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4 md:gap-8 text-[10px] uppercase tracking-[0.3em] text-white/75">
              <span className="inline-flex items-center gap-2"><ShieldCheck size={12} className="text-orion-gold" /> Cadastur ativo</span>
              <span className="inline-flex items-center gap-2"><Globe2 size={12} className="text-orion-gold" /> +30 destinos</span>
              <span className="inline-flex items-center gap-2"><Wallet size={12} className="text-orion-gold" /> A partir de R$ 6.857 · 2 sem</span>
              <span className="inline-flex items-center gap-2"><Clock size={12} className="text-orion-gold" /> Suporte 24h</span>
            </div>
          </div>
        </section>

        {/* TRUST STRIP */}
        <section className="border-y border-[#EFEAE0] bg-[#FAF7F0]">
          <div className="container-orion py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {TRUST.map((t, i) => (
              <div key={i} className="flex flex-col items-center gap-2" data-testid={`int-trust-${i}`}>
                <t.icon size={22} className="text-orion-orange" />
                <div className="font-serif text-orion-purple text-sm md:text-base">{t.title}</div>
                <div className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-[#594A63] leading-snug">{t.body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* PROGRAM TYPES */}
        <section id="programas" className="container-orion py-20 md:py-28">
          <div className="max-w-2xl mx-auto mb-14 text-center">
            <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">O que você pode viver</div>
            <h2 className="font-serif text-3xl md:text-5xl text-orion-purple leading-tight">
              Seis jeitos de morar no exterior — cada um com o seu propósito.
            </h2>
            <p className="mt-4 text-[#594A63] text-base md:text-lg leading-relaxed">
              A gente escuta antes de indicar. Você conta o que sonha e o que cabe no orçamento —
              a consultoria devolve o programa certo pra hora certa da sua vida.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROGRAMS.map((p, i) => (
              <div
                key={i}
                className="group relative overflow-hidden border border-[#EFEAE0] p-7 hover:border-orion-orange hover:shadow-[0_18px_40px_rgba(44,17,57,0.12)] transition-all bg-white text-center"
                data-testid={`int-program-${i}`}
              >
                <span className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-orion-orange/10 group-hover:bg-orion-orange/20 transition-colors" aria-hidden />
                <p.icon size={30} className="text-orion-orange mb-4 mx-auto group-hover:scale-110 transition-transform relative" strokeWidth={1.6} />
                <h3 className="font-serif text-xl text-orion-purple mb-3 leading-snug relative">{p.title}</h3>
                <p className="text-sm text-[#594A63] leading-relaxed relative">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* DESTINATIONS with pricing */}
        <section id="destinos" className="bg-[#0d0526] text-white py-20 md:py-28 relative overflow-hidden">
          <span className="absolute -top-24 -left-24 w-[400px] h-[400px] rounded-full bg-orion-gold/15 blur-3xl" aria-hidden />
          <span className="absolute -bottom-24 -right-24 w-[400px] h-[400px] rounded-full bg-orion-orange/15 blur-3xl" aria-hidden />
          <div className="relative container-orion">
            <div className="max-w-2xl mx-auto mb-14 text-center">
              <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Destinos Orion</div>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight">
                Onde a sua história vai virar outra?
              </h2>
              <p className="mt-4 text-white/75 text-base md:text-lg leading-relaxed">
                Cada destino tem seu jeito — a gente ajuda a descobrir qual bate com você.
                Programas de curta duração (2 semanas) marcados como Intercâmbio Turístico ·
                experiência experimental; longa duração e programas personalizados sob consulta.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="int-destinations">
              {DESTINATIONS.map((d, i) => (
                <div
                  key={`${d.country}-${d.city}`}
                  className="int-dest-card relative overflow-hidden"
                  style={{ "--dest-color": d.color }}
                  data-testid={`int-dest-${i}`}
                >
                  <div className="int-dest-top">
                    <span className="int-dest-flag" aria-hidden>{d.flag}</span>
                    <div className="int-dest-tag-wrap">
                      <span className="int-dest-tag">{d.tag}</span>
                      {d.tagSub && <span className="int-dest-tagsub">{d.tagSub}</span>}
                    </div>
                  </div>
                  <div className="int-dest-body">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-orion-gold font-semibold mb-1">
                      Intercâmbio em
                    </div>
                    <h3 className="font-serif text-2xl leading-tight">{d.country}</h3>
                    <div className="text-[12px] text-white/70 mt-1">{d.city}</div>

                    <div className="mt-5">
                      {d.programType === "consulta" ? (
                        <>
                          <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">
                            Programa personalizado
                          </div>
                          <div className="font-serif text-2xl text-orion-gold mt-1">{d.from}</div>
                        </>
                      ) : (
                        <>
                          <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">A partir de</div>
                          <div className="font-serif text-3xl text-white mt-1">{d.from}</div>
                          <div className="text-[11px] text-white/60 uppercase tracking-[0.16em] mt-1">
                            / programa de {d.duration}
                          </div>
                        </>
                      )}
                      <div className="mt-2 text-[11px] text-orion-gold/90 font-semibold leading-snug">
                        Parcelamento facilitado
                        <span className="block text-[10px] text-white/60 font-normal normal-case tracking-normal mt-0.5">
                          (pagamento até 30 dias antes do início)
                        </span>
                      </div>
                    </div>

                    <div className="mt-5 pt-4 border-t border-white/10 space-y-2 text-[13px] text-white/85">
                      <div className="flex items-start gap-2">
                        <Sparkles size={13} className="text-orion-gold mt-0.5 shrink-0" />
                        <span>{d.highlight}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Clock size={13} className="text-orion-gold mt-0.5 shrink-0" />
                        <span>{d.duration}</span>
                      </div>
                    </div>

                    <button
                      onClick={() => setQuoteOpen(true)}
                      className="int-dest-btn mt-6"
                      data-testid={`int-dest-btn-${i}`}
                    >
                      {d.programType === "consulta" ? "Solicitar consulta" : "Quero este destino"} <ArrowRight size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-[12px] text-white/50 mt-8 italic max-w-2xl mx-auto leading-relaxed">
              * Valores promocionais de referência (base Egali · fev/2026), sujeitos a variação cambial e disponibilidade da escola. Sempre confirme na consultoria gratuita.
            </p>
          </div>
        </section>

        {/* WHAT'S INCLUDED */}
        <section className="bg-[#2C1139] text-white overflow-hidden">
          <div className="container-orion py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">
                O que sua consultoria inclui
              </div>
              <h2 className="font-serif text-3xl md:text-5xl leading-[1.05]">
                Você chega no destino sem susto — porque a Orion cuidou de tudo aqui.
              </h2>
              <p className="mt-5 text-white/85 text-base md:text-lg leading-relaxed">
                Um intercâmbio bom começa antes do avião decolar. A gente organiza cada peça —
                da matrícula ao chip internacional — pra você foco no que importa: aprender,
                viver e voltar diferente.
              </p>
              <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-white/90">
                {INCLUDED.map((item, i) => (
                  <li key={i} className="flex items-start gap-2" data-testid={`int-inc-${i}`}>
                    <span className="text-orion-gold mt-1 shrink-0">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-9">
                <button
                  onClick={() => setQuoteOpen(true)}
                  className="inline-flex items-center gap-2 bg-orion-gold text-orion-purple px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#c19a3f] transition-all shadow-[0_14px_38px_rgba(216,167,73,0.45)] hover:-translate-y-0.5"
                  data-testid="int-cta-included"
                >
                  <ClipboardList size={14} /> Solicitar Consultoria Gratuita
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img src={LIB_IMG} alt="Biblioteca no exterior" loading="lazy" className="w-full aspect-[3/4] object-cover rounded-lg shadow-[0_18px_40px_rgba(0,0,0,0.35)]" />
              <img src={CITY_IMG} alt="Cidade universitária" loading="lazy" className="w-full aspect-[3/4] object-cover rounded-lg shadow-[0_18px_40px_rgba(0,0,0,0.35)] mt-8" />
            </div>
          </div>
        </section>

        {/* HOW WE OPERATE */}
        <section className="container-orion py-20 md:py-28">
          <div className="max-w-2xl mx-auto mb-12 text-center">
            <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Como funciona</div>
            <h2 className="font-serif text-3xl md:text-5xl text-orion-purple leading-tight">
              Da primeira conversa ao pouso no destino, em 4 passos.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW.map((h, i) => (
              <div key={i} className="relative p-7 border-l-4 border-orion-orange bg-[#FAF7F0] text-center" data-testid={`int-step-${i}`}>
                <div className="font-serif text-4xl text-orion-orange/40 mb-3">{h.step}</div>
                <h3 className="font-serif text-xl text-orion-purple mb-2">{h.title}</h3>
                <p className="text-sm text-[#594A63] leading-relaxed">{h.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="relative bg-gradient-to-br from-orion-gold via-[#c19a3f] to-[#8a6d24] text-white overflow-hidden">
          <span className="absolute inset-0 opacity-25" aria-hidden style={{ backgroundImage: `url(${CTA_IMG})`, backgroundSize: "cover" }} />
          <span className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" aria-hidden />
          <div className="relative container-orion py-20 md:py-28 text-center max-w-3xl">
            <Plane size={30} className="mx-auto text-white mb-5" />
            <h2 className="font-serif text-3xl md:text-5xl leading-tight">
              O mundo tá esperando. Vamos começar?
            </h2>
            <p className="mt-5 text-white/95 text-base md:text-lg">
              Consultoria gratuita, sem compromisso. Conte o seu momento — a Orion volta
              com o programa que vai transformar o próximo ano da sua vida.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setQuoteOpen(true)}
                className="inline-flex items-center gap-2 bg-white text-orion-purple px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-orion-cream transition-all hover:-translate-y-0.5 shadow-[0_14px_38px_rgba(0,0,0,0.25)]"
                data-testid="int-cta-bottom"
              >
                <ClipboardList size={14} /> Quero Falar com a Consultoria
              </button>
              <Link
                to="/sobre"
                className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-white/10 transition-colors"
              >
                Conhecer a Orion
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      {quoteOpen && <SchoolQuoteModal onClose={() => setQuoteOpen(false)} variant="intercambio" />}

      <style>{`
        .int-dest-card {
          border-radius: 10px;
          background: linear-gradient(160deg, rgba(255,255,255,0.04), rgba(0,0,0,0.4));
          border: 1px solid rgba(255,255,255,0.08);
          transition: transform 280ms ease, border-color 280ms ease, box-shadow 280ms ease;
        }
        .int-dest-card:hover {
          transform: translateY(-4px);
          border-color: var(--dest-color);
          box-shadow: 0 22px 44px -18px var(--dest-color), 0 0 40px -12px var(--dest-color);
        }
        .int-dest-top {
          position: relative;
          height: 92px;
          background: linear-gradient(135deg, var(--dest-color), color-mix(in srgb, var(--dest-color) 65%, black));
          border-radius: 10px 10px 0 0;
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 20px;
          overflow: hidden;
        }
        .int-dest-top::before {
          content: ""; position: absolute; inset: 0;
          background: radial-gradient(circle at top right, rgba(255,255,255,0.25), transparent 60%);
        }
        .int-dest-flag {
          font-size: 40px; line-height: 1; position: relative;
          filter: drop-shadow(0 4px 12px rgba(0,0,0,0.4));
        }
        .int-dest-tag-wrap {
          position: relative;
          display: flex; flex-direction: column; align-items: flex-end; gap: 3px;
        }
        .int-dest-tag {
          font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase;
          background: rgba(0,0,0,0.35); color: #fff; padding: 5px 10px;
          border-radius: 999px; font-weight: 700;
          backdrop-filter: blur(4px);
        }
        .int-dest-tagsub {
          font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase;
          background: rgba(255,255,255,0.12); color: #fff; padding: 3px 8px;
          border-radius: 999px; font-weight: 600;
          backdrop-filter: blur(4px);
        }
        .int-dest-body { padding: 22px 22px 24px 22px; }
        .int-dest-btn {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase; font-weight: 800;
          padding: 10px 16px; border-radius: 999px;
          background: rgba(255,255,255,0.08); color: #fff;
          border: 1px solid rgba(255,255,255,0.2);
          cursor: pointer; transition: all 220ms ease;
        }
        .int-dest-btn:hover {
          background: var(--dest-color); border-color: var(--dest-color);
          transform: translateX(2px);
        }
      `}</style>
    </div>
  );
}
